import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
    LayoutDashboard, Package, Wrench, ShoppingCart, Users, 
    Plus, Pencil, Trash2, X, Check, Upload, Image, KeyRound, Eye, EyeOff, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const ImageUploader = ({ currentUrl, onImageUploaded }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(currentUrl || '');
    const [manualUrl, setManualUrl] = useState('');
    const [mode, setMode] = useState('upload'); // 'upload' or 'url'
    const fileInputRef = React.useRef(null);

    React.useEffect(() => {
        setPreview(currentUrl || '');
    }, [currentUrl]);

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Seleziona un file immagine (JPG, PNG, GIF, WebP)');
            return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error('Immagine troppo grande. Max 5MB');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API}/api/upload/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const imageUrl = `${API}${response.data.url}`;
            setPreview(imageUrl);
            onImageUploaded(imageUrl);
            toast.success('Immagine caricata!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nel caricamento');
        } finally {
            setUploading(false);
        }
    };

    const handleManualUrl = () => {
        if (manualUrl.trim()) {
            setPreview(manualUrl.trim());
            onImageUploaded(manualUrl.trim());
            setManualUrl('');
        }
    };

    return (
        <div className="mt-1 space-y-3">
            {/* Mode toggle */}
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setMode('upload')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        mode === 'upload' ? 'bg-primary text-white' : 'bg-surface-elevated text-muted border border-border'
                    }`}
                    data-testid="upload-mode-btn"
                >
                    <Upload className="w-4 h-4 inline mr-1" /> Carica foto
                </button>
                <button
                    type="button"
                    onClick={() => setMode('url')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        mode === 'url' ? 'bg-primary text-white' : 'bg-surface-elevated text-muted border border-border'
                    }`}
                    data-testid="url-mode-btn"
                >
                    <Image className="w-4 h-4 inline mr-1" /> URL immagine
                </button>
            </div>

            {mode === 'upload' ? (
                <div
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                        uploading ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-surface-elevated'
                    }`}
                    data-testid="image-dropzone"
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="image-file-input"
                    />
                    {uploading ? (
                        <div className="text-primary">
                            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                            <p className="text-sm">Caricamento in corso...</p>
                        </div>
                    ) : (
                        <div className="text-muted">
                            <Upload className="w-8 h-8 mx-auto mb-2" />
                            <p className="text-sm font-medium">Clicca per caricare un'immagine</p>
                            <p className="text-xs mt-1">JPG, PNG, GIF, WebP - Max 5MB</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex gap-2">
                    <Input
                        value={manualUrl}
                        onChange={(e) => setManualUrl(e.target.value)}
                        placeholder="https://esempio.com/immagine.jpg"
                        className="bg-surface-elevated border-border flex-1"
                        data-testid="manual-url-input"
                    />
                    <Button type="button" onClick={handleManualUrl} className="bg-primary hover:bg-primary-hover" data-testid="apply-url-btn">
                        Applica
                    </Button>
                </div>
            )}

            {/* Preview */}
            {preview && (
                <div className="relative rounded-lg overflow-hidden border border-border bg-surface-elevated">
                    <img src={preview} alt="Anteprima" className="w-full h-40 object-cover" />
                    <button
                        type="button"
                        onClick={() => { setPreview(''); onImageUploaded(''); }}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-danger/80 text-white flex items-center justify-center hover:bg-danger"
                        data-testid="remove-preview-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
};

const SettingsTab = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword) {
            toast.error('Compila tutti i campi');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('La nuova password deve avere almeno 6 caratteri');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Le password non corrispondono');
            return;
        }

        setSaving(true);
        try {
            await axios.post(`${API}/api/auth/change-password`, {
                current_password: currentPassword,
                new_password: newPassword
            });
            toast.success('Password aggiornata con successo!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nel cambio password');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="animate-fade-in">
            <h1 className="text-2xl font-bold mb-6">Impostazioni</h1>

            <div className="bg-surface border border-border rounded-xl p-6 max-w-lg">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-primary" />
                    Cambia Password
                </h2>

                <div className="space-y-4">
                    <div>
                        <Label htmlFor="current_password">Password attuale</Label>
                        <div className="relative mt-1">
                            <Input
                                id="current_password"
                                type={showCurrent ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="••••••••"
                                className="bg-surface-elevated border-border pr-10"
                                data-testid="current-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrent(!showCurrent)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white"
                            >
                                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="new_password">Nuova password</Label>
                        <div className="relative mt-1">
                            <Input
                                id="new_password"
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="••••••••"
                                className="bg-surface-elevated border-border pr-10"
                                data-testid="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew(!showNew)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white"
                            >
                                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <p className="text-xs text-muted mt-1">Minimo 6 caratteri</p>
                    </div>

                    <div>
                        <Label htmlFor="confirm_password">Conferma nuova password</Label>
                        <Input
                            id="confirm_password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                            className="bg-surface-elevated border-border mt-1"
                            data-testid="confirm-password"
                        />
                    </div>

                    <Button
                        onClick={handleChangePassword}
                        disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                        className="w-full btn-primary"
                        data-testid="save-password-btn"
                    >
                        {saving ? 'Salvataggio...' : 'Aggiorna password'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AdminDashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [services, setServices] = useState([]);
    const [users, setUsers] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [productModal, setProductModal] = useState(false);
    const [serviceModal, setServiceModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingService, setEditingService] = useState(null);

    // Form states
    const [productForm, setProductForm] = useState({
        name: '', description: '', price: '', category: 'accessory', image_url: '', stock: '', brand: '', color: ''
    });
    const [serviceForm, setServiceForm] = useState({
        name: '', description: '', price: '', device_type: 'smartphone', duration_minutes: '60'
    });

    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
            return;
        }
        fetchData();
    }, [isAdmin, navigate]);

    const fetchData = async () => {
        try {
            const [statsRes, productsRes, ordersRes, bookingsRes, servicesRes, usersRes] = await Promise.all([
                axios.get(`${API}/api/admin/stats`),
                axios.get(`${API}/api/products`),
                axios.get(`${API}/api/admin/orders`),
                axios.get(`${API}/api/admin/bookings`),
                axios.get(`${API}/api/repair-services`),
                axios.get(`${API}/api/admin/users`)
            ]);
            setStats(statsRes.data);
            setProducts(productsRes.data);
            setOrders(ordersRes.data);
            setBookings(bookingsRes.data);
            setServices(servicesRes.data);
            setUsers(usersRes.data);
            // Fetch leads separately (non-blocking)
            try {
                const leadsRes = await axios.get(`${API}/api/admin/telecom-leads`);
                setLeads(leadsRes.data);
            } catch { /* ignore if no leads */ }
        } catch (error) {
            toast.error('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProduct = async () => {
        try {
            const data = {
                ...productForm,
                price: parseFloat(productForm.price),
                stock: parseInt(productForm.stock) || 0
            };
            if (editingProduct) {
                await axios.put(`${API}/api/admin/products/${editingProduct.id}`, data);
                toast.success('Prodotto aggiornato');
            } else {
                await axios.post(`${API}/api/admin/products`, data);
                toast.success('Prodotto creato');
            }
            setProductModal(false);
            setEditingProduct(null);
            resetProductForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore');
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Eliminare questo prodotto?')) return;
        try {
            await axios.delete(`${API}/api/admin/products/${id}`);
            toast.success('Prodotto eliminato');
            fetchData();
        } catch (error) {
            toast.error('Errore eliminazione');
        }
    };

    const handleSaveService = async () => {
        try {
            const data = {
                ...serviceForm,
                price: parseFloat(serviceForm.price),
                duration_minutes: parseInt(serviceForm.duration_minutes)
            };
            if (editingService) {
                await axios.put(`${API}/api/admin/repair-services/${editingService.id}`, data);
                toast.success('Servizio aggiornato');
            } else {
                await axios.post(`${API}/api/admin/repair-services`, data);
                toast.success('Servizio creato');
            }
            setServiceModal(false);
            setEditingService(null);
            resetServiceForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore');
        }
    };

    const handleDeleteService = async (id) => {
        if (!window.confirm('Eliminare questo servizio?')) return;
        try {
            await axios.delete(`${API}/api/admin/repair-services/${id}`);
            toast.success('Servizio eliminato');
            fetchData();
        } catch (error) {
            toast.error('Errore eliminazione');
        }
    };

    const handleUpdateOrderStatus = async (orderId, status) => {
        try {
            await axios.put(`${API}/api/admin/orders/${orderId}/status?status=${status}`);
            toast.success('Stato aggiornato');
            fetchData();
        } catch (error) {
            toast.error('Errore aggiornamento');
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm('Eliminare questo ordine dall\'elenco?')) return;
        try {
            await axios.delete(`${API}/api/admin/orders/${orderId}`);
            toast.success('Ordine eliminato');
            fetchData();
        } catch (error) {
            toast.error('Errore eliminazione');
        }
    };

    const handleUpdateBookingStatus = async (bookingId, status) => {
        try {
            await axios.put(`${API}/api/admin/bookings/${bookingId}/status?status=${status}`);
            toast.success('Stato aggiornato');
            fetchData();
        } catch (error) {
            toast.error('Errore aggiornamento');
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        if (!window.confirm('Eliminare questa prenotazione dall\'elenco?')) return;
        try {
            await axios.delete(`${API}/api/admin/bookings/${bookingId}`);
            toast.success('Prenotazione eliminata');
            fetchData();
        } catch (error) {
            toast.error('Errore eliminazione');
        }
    };

    const resetProductForm = () => {
        setProductForm({ name: '', description: '', price: '', category: 'accessory', image_url: '', stock: '', brand: '', color: '' });
    };

    const resetServiceForm = () => {
        setServiceForm({ name: '', description: '', price: '', device_type: 'smartphone', duration_minutes: '60' });
    };

    const openEditProduct = (product) => {
        setEditingProduct(product);
        setProductForm({
            name: product.name,
            description: product.description,
            price: product.price.toString(),
            category: product.category,
            image_url: product.image_url,
            stock: product.stock.toString(),
            brand: product.brand || '',
            color: product.color || ''
        });
        setProductModal(true);
    };

    const openEditService = (service) => {
        setEditingService(service);
        setServiceForm({
            name: service.name,
            description: service.description,
            price: service.price.toString(),
            device_type: service.device_type,
            duration_minutes: service.duration_minutes.toString()
        });
        setServiceModal(true);
    };

    const navItems = [
        { id: 'overview', label: 'Panoramica', icon: LayoutDashboard },
        { id: 'products', label: 'Prodotti', icon: Package },
        { id: 'orders', label: 'Ordini', icon: ShoppingCart },
        { id: 'bookings', label: 'Prenotazioni', icon: Wrench },
        { id: 'services', label: 'Servizi', icon: Wrench },
        { id: 'leads', label: 'Lead Bollette', icon: FileText },
        { id: 'users', label: 'Utenti', icon: Users },
        { id: 'settings', label: 'Impostazioni', icon: KeyRound },
    ];

    const getCatLabel = (cat) => ({ new: 'Nuovo', refurbished: 'Ricondizionato', accessory: 'Accessorio', spare_part: 'Ricambio' }[cat] || cat);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse text-xl">Caricamento...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex">
            {/* Sidebar */}
            <aside className="admin-sidebar hidden md:block">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <LayoutDashboard className="w-5 h-5 text-primary" />
                    Admin Panel
                </h2>
                <nav className="space-y-1">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`admin-nav-item w-full ${activeTab === item.id ? 'active' : ''}`}
                            data-testid={`admin-nav-${item.id}`}
                        >
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Mobile Nav */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-50 p-2">
                <div className="flex justify-around">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`p-2 rounded-lg ${activeTab === item.id ? 'text-primary bg-primary/10' : 'text-muted'}`}
                        >
                            <item.icon className="w-5 h-5" />
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <main className="flex-1 p-6 pb-20 md:pb-6">
                {/* Overview */}
                {activeTab === 'overview' && (
                    <div className="animate-fade-in">
                        <h1 className="text-2xl font-bold mb-6">Panoramica</h1>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            <StatCard title="Prodotti" value={stats?.products_count} icon={Package} />
                            <StatCard title="Ordini" value={stats?.orders_count} icon={ShoppingCart} />
                            <StatCard title="Prenotazioni" value={stats?.bookings_count} icon={Wrench} />
                            <StatCard title="Utenti" value={stats?.users_count} icon={Users} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-surface border border-border rounded-xl p-6">
                                <h3 className="font-semibold mb-2">Ordini in attesa</h3>
                                <p className="text-3xl font-bold text-warning">{stats?.pending_orders}</p>
                            </div>
                            <div className="bg-surface border border-border rounded-xl p-6">
                                <h3 className="font-semibold mb-2">Prenotazioni oggi</h3>
                                <p className="text-3xl font-bold text-primary">{stats?.today_bookings}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Products */}
                {activeTab === 'products' && (
                    <div className="animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="text-2xl font-bold">Prodotti</h1>
                            <Button onClick={() => { resetProductForm(); setEditingProduct(null); setProductModal(true); }} className="btn-primary" data-testid="add-product-btn">
                                <Plus className="w-4 h-4 mr-2" /> Nuovo prodotto
                            </Button>
                        </div>
                        <div className="bg-surface border border-border rounded-xl overflow-hidden">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Prodotto</th>
                                        <th>Categoria</th>
                                        <th>Colore</th>
                                        <th>Prezzo</th>
                                        <th>Stock</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr key={product.id}>
                                            <td className="font-medium">{product.name}</td>
                                            <td><span className="badge badge-primary">{getCatLabel(product.category)}</span></td>
                                            <td>{product.color || '-'}</td>
                                            <td>€{product.price.toFixed(2)}</td>
                                            <td>{product.stock}</td>
                                            <td>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="ghost" onClick={() => openEditProduct(product)}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-danger" onClick={() => handleDeleteProduct(product.id)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Orders */}
                {activeTab === 'orders' && (
                    <div className="animate-fade-in">
                        <h1 className="text-2xl font-bold mb-6">Ordini</h1>
                        <div className="space-y-4">
                            {orders.map((order) => {
                                const statusLabel = { pending: 'In attesa', confirmed: 'Confermato', shipped: 'Spedito', delivered: 'Consegnato', cancelled: 'Annullato' };
                                const statusColor = { pending: 'badge-warning', confirmed: 'badge-success', shipped: 'badge-primary', delivered: 'badge-success', cancelled: 'badge-danger' };
                                const nextSteps = {
                                    pending: [{ label: 'Conferma', value: 'confirmed', color: 'text-success border-success/50 hover:bg-success/10' }],
                                    confirmed: [{ label: 'Spedito', value: 'shipped', color: 'text-primary border-primary/50 hover:bg-primary/10' }],
                                    shipped: [{ label: 'Consegnato', value: 'delivered', color: 'text-success border-success/50 hover:bg-success/10' }],
                                };
                                return (
                                    <div key={order.id} className="bg-surface border border-border rounded-xl p-4">
                                        <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                                            <div>
                                                <p className="font-medium">#{order.id.slice(0, 8)}</p>
                                                <p className="text-sm text-muted">{order.user_email}</p>
                                            </div>
                                            <div className="flex flex-col items-end gap-2">
                                                <span className={`badge ${statusColor[order.status] || 'badge-muted'}`}>{statusLabel[order.status] || order.status}</span>
                                                <div className="flex gap-1">
                                                    {(nextSteps[order.status] || []).map(step => (
                                                        <Button key={step.value} size="sm" variant="outline" className={`text-xs h-7 ${step.color}`} onClick={() => handleUpdateOrderStatus(order.id, step.value)}>
                                                            {step.label}
                                                        </Button>
                                                    ))}
                                                    {order.status !== 'cancelled' && order.status !== 'delivered' && (
                                                        <Button size="sm" variant="outline" className="text-xs h-7 text-danger border-danger/50 hover:bg-danger/10" onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}>
                                                            Annulla
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-sm text-muted">{order.items.length} prodotti</p>
                                        <div className="flex justify-between items-center">
                                            <p className="text-lg font-bold text-primary">€{order.total.toFixed(2)}</p>
                                            {(order.status === 'delivered' || order.status === 'cancelled') && (
                                                <Button size="sm" variant="ghost" className="text-danger hover:bg-danger/10" onClick={() => handleDeleteOrder(order.id)} data-testid={`delete-order-${order.id}`}>
                                                    <Trash2 className="w-4 h-4 mr-1" /> Elimina
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {orders.length === 0 && <div className="text-center py-12 text-muted">Nessun ordine</div>}
                        </div>
                    </div>
                )}

                {/* Bookings */}
                {activeTab === 'bookings' && (
                    <div className="animate-fade-in">
                        <h1 className="text-2xl font-bold mb-6">Prenotazioni</h1>
                        <div className="space-y-4">
                            {bookings.map((booking) => {
                                const statusLabel = { confirmed: 'Confermato', completed: 'Completato', cancelled: 'Annullato' };
                                const statusColor = { confirmed: 'badge-success', completed: 'badge-primary', cancelled: 'badge-danger' };
                                return (
                                    <div key={booking.id} className="bg-surface border border-border rounded-xl p-4">
                                        <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                                            <div>
                                                <p className="font-medium">{booking.service_name}</p>
                                                <p className="text-sm text-muted">{booking.customer_name} - {booking.customer_email}</p>
                                                <p className="text-sm">{booking.date} alle {booking.time_slot}</p>
                                                {booking.customer_phone && <p className="text-sm text-muted">Tel: {booking.customer_phone}</p>}
                                            </div>
                                            <div className="flex flex-col items-end gap-2">
                                                <span className={`badge ${statusColor[booking.status] || 'badge-muted'}`}>{statusLabel[booking.status] || booking.status}</span>
                                                <div className="flex gap-1">
                                                    {booking.status !== 'completed' && (
                                                        <Button size="sm" variant="outline" className="text-xs h-7 border-success/50 text-success hover:bg-success/10" onClick={() => handleUpdateBookingStatus(booking.id, 'completed')}>
                                                            Completato
                                                        </Button>
                                                    )}
                                                    {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                                                        <Button size="sm" variant="outline" className="text-xs h-7 border-danger/50 text-danger hover:bg-danger/10" onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}>
                                                            Annulla
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-sm text-muted">Dispositivo: {booking.device_model}</p>
                                        <div className="flex justify-between items-center">
                                            <p className="text-lg font-bold text-primary">€{booking.service_price.toFixed(2)}</p>
                                            {(booking.status === 'completed' || booking.status === 'cancelled') && (
                                                <Button size="sm" variant="ghost" className="text-danger hover:bg-danger/10" onClick={() => handleDeleteBooking(booking.id)} data-testid={`delete-booking-${booking.id}`}>
                                                    <Trash2 className="w-4 h-4 mr-1" /> Elimina
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {bookings.length === 0 && <div className="text-center py-12 text-muted">Nessuna prenotazione</div>}
                        </div>
                    </div>
                )}

                {/* Services */}
                {activeTab === 'services' && (
                    <div className="animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="text-2xl font-bold">Servizi di Riparazione</h1>
                            <Button onClick={() => { resetServiceForm(); setEditingService(null); setServiceModal(true); }} className="btn-primary" data-testid="add-service-btn">
                                <Plus className="w-4 h-4 mr-2" /> Nuovo servizio
                            </Button>
                        </div>
                        <div className="bg-surface border border-border rounded-xl overflow-hidden">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Servizio</th>
                                        <th>Tipo</th>
                                        <th>Prezzo</th>
                                        <th>Durata</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {services.map((service) => (
                                        <tr key={service.id}>
                                            <td className="font-medium">{service.name}</td>
                                            <td>{service.device_type}</td>
                                            <td>€{service.price.toFixed(2)}</td>
                                            <td>{service.duration_minutes} min</td>
                                            <td>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="ghost" onClick={() => openEditService(service)}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-danger" onClick={() => handleDeleteService(service.id)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Leads Bollette */}
                {activeTab === 'leads' && (
                    <div className="animate-fade-in">
                        <h1 className="text-2xl font-bold mb-6">Lead Confronto Bollette ({leads.length})</h1>
                        <div className="space-y-4">
                            {leads.map((lead) => (
                                <div key={lead.id} className="bg-surface border border-border rounded-xl p-4">
                                    <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                                        <div>
                                            <p className="font-medium">{lead.customer_name || 'Anonimo'}</p>
                                            {lead.customer_email && <p className="text-sm"><a href={`mailto:${lead.customer_email}`} className="text-primary hover:underline">{lead.customer_email}</a></p>}
                                            {lead.phone_number && <p className="text-sm"><a href={`tel:${lead.phone_number}`} className="hover:text-primary">{lead.phone_number}</a></p>}
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-warning">€{lead.monthly_cost.toFixed(2)}/mese</p>
                                            <span className="badge badge-primary">{lead.current_operator}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
                                        <span>Tipo: {lead.bill_type === 'mobile' ? 'Mobile' : lead.bill_type === 'fibra' ? 'Fibra' : 'Mobile+Fibra'}</span>
                                        {lead.bill_url && (
                                            <a href={`${API}${lead.bill_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                                                <FileText className="w-4 h-4" /> Vedi bolletta
                                            </a>
                                        )}
                                        <span className="ml-auto">{new Date(lead.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>
                            ))}
                            {leads.length === 0 && <div className="text-center py-12 text-muted bg-surface rounded-xl border border-border">Nessun lead ricevuto</div>}
                        </div>
                    </div>
                )}

                {/* Users */}
                {activeTab === 'users' && (
                    <div className="animate-fade-in">
                        <h1 className="text-2xl font-bold mb-6">Utenti Registrati ({users.length})</h1>
                        <div className="bg-surface border border-border rounded-xl overflow-hidden">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th>Telefono</th>
                                        <th>Ruolo</th>
                                        <th>Data Registrazione</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u, idx) => (
                                        <tr key={u.email || idx}>
                                            <td className="font-medium">{u.name}</td>
                                            <td>
                                                <a href={`mailto:${u.email}`} className="text-primary hover:underline">{u.email}</a>
                                            </td>
                                            <td>
                                                {u.phone ? (
                                                    <a href={`tel:${u.phone}`} className="hover:text-primary">{u.phone}</a>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${u.role === 'admin' ? 'badge-primary' : 'badge-success'}`}>
                                                    {u.role === 'admin' ? 'Admin' : 'Cliente'}
                                                </span>
                                            </td>
                                            <td className="text-muted text-sm">
                                                {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {users.length === 0 && (
                                <div className="text-center py-12 text-muted">Nessun utente registrato</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Settings */}
                {activeTab === 'settings' && (
                    <SettingsTab />
                )}
            </main>

            {/* Product Modal */}
            <Dialog open={productModal} onOpenChange={setProductModal}>
                <DialogContent className="bg-surface border-border max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Nome</Label>
                            <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                        </div>
                        <div>
                            <Label>Descrizione</Label>
                            <Textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Prezzo (€)</Label>
                                <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                            </div>
                            <div>
                                <Label>Stock</Label>
                                <Input type="number" value={productForm.stock} onChange={(e) => setProductForm({...productForm, stock: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label>Categoria</Label>
                            <Select value={productForm.category} onValueChange={(v) => setProductForm({...productForm, category: v})}>
                                <SelectTrigger className="bg-surface-elevated border-border mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface border-border">
                                    <SelectItem value="new">Nuovo</SelectItem>
                                    <SelectItem value="refurbished">Ricondizionato</SelectItem>
                                    <SelectItem value="accessory">Accessorio</SelectItem>
                                    <SelectItem value="spare_part">Ricambio</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Immagine prodotto</Label>
                            <ImageUploader
                                currentUrl={productForm.image_url}
                                onImageUploaded={(url) => setProductForm({...productForm, image_url: url})}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Brand</Label>
                                <Input value={productForm.brand} onChange={(e) => setProductForm({...productForm, brand: e.target.value})} className="bg-surface-elevated border-border mt-1" placeholder="es. Apple, Samsung" />
                            </div>
                            <div>
                                <Label>Colore</Label>
                                <Input value={productForm.color} onChange={(e) => setProductForm({...productForm, color: e.target.value})} className="bg-surface-elevated border-border mt-1" placeholder="es. Nero, Bianco, Blu" />
                            </div>
                        </div>
                        <Button onClick={handleSaveProduct} className="w-full btn-primary">Salva</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Service Modal */}
            <Dialog open={serviceModal} onOpenChange={setServiceModal}>
                <DialogContent className="bg-surface border-border max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Nome</Label>
                            <Input value={serviceForm.name} onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                        </div>
                        <div>
                            <Label>Descrizione</Label>
                            <Textarea value={serviceForm.description} onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Prezzo (€)</Label>
                                <Input type="number" step="0.01" value={serviceForm.price} onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                            </div>
                            <div>
                                <Label>Durata (min)</Label>
                                <Input type="number" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm({...serviceForm, duration_minutes: e.target.value})} className="bg-surface-elevated border-border mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label>Tipo dispositivo</Label>
                            <Select value={serviceForm.device_type} onValueChange={(v) => setServiceForm({...serviceForm, device_type: v})}>
                                <SelectTrigger className="bg-surface-elevated border-border mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-surface border-border">
                                    <SelectItem value="smartphone">Smartphone</SelectItem>
                                    <SelectItem value="tablet">Tablet</SelectItem>
                                    <SelectItem value="laptop">Computer</SelectItem>
                                    <SelectItem value="console">Console</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={handleSaveService} className="w-full btn-primary">Salva</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const StatCard = ({ title, value, icon: Icon }) => (
    <div className="bg-surface border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
            <span className="text-muted text-sm">{title}</span>
            <Icon className="w-5 h-5 text-primary" />
        </div>
        <p className="text-2xl font-bold">{value || 0}</p>
    </div>
);

export default AdminDashboard;
